import axiosInstance from "../../utils/axiosConfig";
import { otcSubtypesParam as resolveOtcSubtypes } from "../api/adminOtcSubtypes";
import { compactParams } from "../api/params";
import type { AdminTransactionExportContext } from "./exportContext";

export const EXPORT_PAGE_LIMIT = 100;
export const MAX_EXPORT_PAGES = 200;

export type TransactionExportBundle = {
  summary: unknown;
  transactions: Record<string, unknown>[];
  meta: {
    type: AdminTransactionExportContext["type"];
    startDate: string;
    endDate: string;
    orderSubtype?: string;
    billSubtypes?: string;
    otcSubtypes?: string;
    businessTxType?: string;
    businessStatus?: string;
    businessId?: string;
    swapStatus?: string;
  };
};

type ApiEnvelope<T> = { status?: string; message?: string; data?: T };

type TransactionsPayload = {
  transactions?: unknown[];
  pagination?: {
    currentPage?: number;
    totalPages?: number;
    totalCount?: number;
    limit?: number;
  };
};

function orderSubtypesParam(
  ctx: AdminTransactionExportContext
): string | undefined {
  if (ctx.type !== "orders") return undefined;
  const s = ctx.orderSubtype;
  if (!s || s === "all") return undefined;
  return s;
}

function billSubtypesParam(
  ctx: AdminTransactionExportContext
): string | undefined {
  if (ctx.type !== "billpayment") return undefined;
  const t = ctx.billSubtypes?.trim();
  return t || undefined;
}

/**
 * Required by `transactions/data` whenever type=otc, and a single value only.
 * Must NOT be sent to `transactions/summaries`, which rejects it.
 */
function otcSubtypesParam(
  ctx: AdminTransactionExportContext
): string | undefined {
  if (ctx.type !== "otc") return undefined;
  return resolveOtcSubtypes(ctx.otcSubtype);
}

/** Fees: `status` filters by TRX swap status on `transactions/data` only. */
function spreadStatusParam(
  ctx: AdminTransactionExportContext
): string | undefined {
  if (ctx.type !== "spread") return undefined;
  return ctx.swapStatus || undefined;
}

type BusinessTransactionsPayload = {
  transactions?: unknown[];
  meta?: {
    currentPage?: number;
    limit?: number;
    totalTransactions?: number;
    totalPages?: number;
  };
};

/**
 * Businesses live under `api/businesses/admin/*` with `from`/`to` params and a
 * `meta` (not `pagination`) block, so they get their own fetch path.
 */
async function fetchBusinessExportBundle(
  ctx: Extract<AdminTransactionExportContext, { type: "businesses" }>,
  range: { startDate: string; endDate: string }
): Promise<TransactionExportBundle> {
  const { startDate, endDate } = range;
  const status =
    ctx.businessStatus && ctx.businessStatus !== "all"
      ? ctx.businessStatus
      : undefined;

  const summaryRes = await axiosInstance.get<ApiEnvelope<unknown>>(
    "api/businesses/admin/summary",
    {
      params: compactParams({
        from: startDate,
        to: endDate,
        businessId: ctx.businessId,
      }),
    }
  );

  const transactions: Record<string, unknown>[] = [];
  let totalPages = 1;

  for (let page = 1; page <= totalPages; page++) {
    if (page > MAX_EXPORT_PAGES) {
      throw new Error(
        `Export is limited to ${MAX_EXPORT_PAGES} pages (up to ${
          MAX_EXPORT_PAGES * EXPORT_PAGE_LIMIT
        } rows). Narrow the date range.`
      );
    }

    const dataRes = await axiosInstance.get<
      ApiEnvelope<BusinessTransactionsPayload>
    >("api/businesses/admin/transactions", {
      params: compactParams({
        type: ctx.businessTxType,
        status,
        businessId: ctx.businessId,
        page,
        limit: EXPORT_PAGE_LIMIT,
        from: startDate,
        to: endDate,
      }),
    });

    const payload = dataRes.data?.data;
    const batch = payload?.transactions ?? [];

    if (page === 1) {
      totalPages = Math.max(1, Number(payload?.meta?.totalPages) || 1);
    }

    for (const row of batch) {
      if (row && typeof row === "object") {
        transactions.push(row as Record<string, unknown>);
      }
    }

    if (batch.length === 0) break;
  }

  return {
    summary: summaryRes.data?.data,
    transactions,
    meta: {
      type: "businesses",
      startDate,
      endDate,
      businessTxType: ctx.businessTxType,
      ...(status ? { businessStatus: status } : {}),
      ...(ctx.businessId ? { businessId: ctx.businessId } : {}),
    },
  };
}

/**
 * Fetches summary + all transaction pages for the given context and date range.
 */
export async function fetchTransactionExportBundle(
  ctx: AdminTransactionExportContext,
  range: { startDate: string; endDate: string }
): Promise<TransactionExportBundle> {
  if (ctx.type === "businesses") {
    return fetchBusinessExportBundle(ctx, range);
  }

  const { startDate, endDate } = range;
  const type = ctx.type;

  const summaryParams = compactParams({
    type,
    startDate,
    endDate,
    orderSubtypes: orderSubtypesParam(ctx),
  });

  const summaryRes = await axiosInstance.get<ApiEnvelope<unknown>>(
    "api/admin/transactions/summaries",
    { params: summaryParams }
  );
  const summary = summaryRes.data?.data;

  const transactions: Record<string, unknown>[] = [];
  let totalPages = 1;

  for (let page = 1; page <= totalPages; page++) {
    if (page > MAX_EXPORT_PAGES) {
      throw new Error(
        `Export is limited to ${MAX_EXPORT_PAGES} pages (up to ${
          MAX_EXPORT_PAGES * EXPORT_PAGE_LIMIT
        } rows). Narrow the date range.`
      );
    }

    const dataParams = compactParams({
      type,
      page,
      limit: EXPORT_PAGE_LIMIT,
      startDate,
      endDate,
      orderSubtypes: orderSubtypesParam(ctx),
      billSubtypes: billSubtypesParam(ctx),
      otcSubtypes: otcSubtypesParam(ctx),
      status: spreadStatusParam(ctx),
    });

    const dataRes = await axiosInstance.get<ApiEnvelope<TransactionsPayload>>(
      "api/admin/transactions/data",
      { params: dataParams }
    );

    const payload = dataRes.data?.data;
    const batch = payload?.transactions ?? [];
    const pag = payload?.pagination;

    if (page === 1) {
      totalPages = Math.max(1, Number(pag?.totalPages) || 1);
    }

    for (const row of batch) {
      if (row && typeof row === "object") {
        transactions.push(row as Record<string, unknown>);
      }
    }

    if (batch.length === 0) break;
  }

  return {
    summary,
    transactions,
    meta: {
      type,
      startDate,
      endDate,
      ...(ctx.type === "orders" &&
      ctx.orderSubtype &&
      ctx.orderSubtype !== "all"
        ? { orderSubtype: ctx.orderSubtype }
        : {}),
      ...(ctx.type === "billpayment" && ctx.billSubtypes?.trim()
        ? { billSubtypes: ctx.billSubtypes.trim() }
        : {}),
      ...(ctx.type === "otc"
        ? { otcSubtypes: resolveOtcSubtypes(ctx.otcSubtype) }
        : {}),
      ...(ctx.type === "spread" && ctx.swapStatus
        ? { swapStatus: ctx.swapStatus }
        : {}),
    },
  };
}
